/**
 * Organiser discovery panel — find NEW organisers by topic.
 *
 * Wraps `POST /api/mgmt/series/discover-by-topic`. This is the only discovery
 * path that can surface an account we have never seen mentioned anywhere:
 * `mgmt/series/discover` only mines @handles out of content already ingested,
 * and the Places sweep needs a physical address — which arts collectives, land
 * trusts and volunteer aggregators often don't have.
 *
 * Every lookup is billable (Apify posts, or CSE requests when configured), so the
 * panel defaults to a DRY RUN and makes a real run an explicit second click.
 * Discovered rows are created as `candidate`, never active.
 */
import { useState } from 'react';
import { api } from '../lib/api';

interface Report {
  dryRun: boolean;
  provider: string;
  topicsRun: string[];
  lookupsRun: number;
  queriesRun: number;
  hashtagRuns: number;
  postsScanned: number;
  candidatesFound: number;
  alreadyKnown: number;
  considered: number;
  llmUsed: boolean;
  seriesCreated: number;
  venuesCreated: number;
  dismissed: number;
  lookupsCapped: boolean;
  created: Array<{ rowKey: string; name: string; handle?: string; kind: string; confidence: number; topic: string; reason: string }>;
  rejected: Array<{ key: string; kind: string; reason: string; topic: string }>;
  errors: Array<{ query: string; message: string }>;
}

const TOPICS = ['scene', 'arts', 'crafts', 'markets', 'vintage', 'plants', 'nature', 'volunteer', 'puzzles'] as const;

export default function OrganiserDiscoveryPanel() {
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState<Report | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [maxLookups, setMaxLookups] = useState(15);

  const run = async (dryRun: boolean) => {
    if (!dryRun && !window.confirm(
      `Run a REAL organiser discovery sweep?\n\n` +
      `Up to ${maxLookups} billable lookups will run and any new organisers will be ` +
      `created as candidates needing your approval.`
    )) return;
    setBusy(true);
    setError(null);
    try {
      const body: Record<string, unknown> = { dryRun, maxLookups };
      if (selected.length) body.topics = selected;
      setReport(await api.post<Report>('/api/mgmt/series/discover-by-topic', body));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Organiser discovery failed.');
      setReport(null);
    } finally {
      setBusy(false);
    }
  };

  const toggleTopic = (id: string) => {
    setSelected((cur) => (cur.includes(id) ? cur.filter((t) => t !== id) : [...cur, id]));
  };

  return (
    <section className="mb-5">
      <h2 className="h4">Find new organisers</h2>
      <p className="form-text mt-0">
        Searches by topic for organisers we do not track yet — arts collectives, land trusts,
        volunteer aggregators, market and puzzle organisers, most of which have no fixed venue and
        therefore never appear in a Places sweep. Results are created as candidates and still need
        approving under Series. Each lookup is billable, so start with a dry run.
      </p>

      <div className="d-flex flex-wrap gap-3 align-items-end mb-3">
        <div>
          <span className="form-label d-block mb-1">Topics</span>
          <div className="d-flex flex-wrap gap-2">
            {TOPICS.map((id) => (
              <button
                key={id}
                type="button"
                className={`btn btn-sm ${selected.includes(id) ? 'btn-primary' : 'btn-outline-secondary'}`}
                aria-pressed={selected.includes(id)}
                onClick={() => toggleTopic(id)}
              >
                {id}
              </button>
            ))}
          </div>
          <div className="form-text">None selected = all topics.</div>
        </div>

        <div>
          <label htmlFor="od-max" className="form-label">Max billable lookups</label>
          <input
            id="od-max"
            type="number"
            min={1}
            max={400}
            className="form-control form-control-sm mh-input-narrow"
            value={maxLookups}
            onChange={(e) => setMaxLookups(Math.max(1, Math.min(400, Number(e.target.value) || 1)))}
          />
        </div>

        <div className="d-flex gap-2">
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => void run(true)} disabled={busy}>
            {busy ? 'Working…' : 'Dry run'}
          </button>
          <button type="button" className="btn btn-sm btn-primary" onClick={() => void run(false)} disabled={busy}>
            {busy ? 'Working…' : 'Run and create candidates'}
          </button>
        </div>
      </div>

      {error && <div className="alert alert-warning" role="alert">{error}</div>}

      {report && (
        <div className="border rounded p-3">
          <div className="small text-body-secondary mb-2">
            {report.dryRun ? 'DRY RUN (nothing saved)' : 'Saved as candidates'} · provider <code>{report.provider}</code> ·
            ' ' lookups {report.lookupsRun}{report.lookupsCapped ? ' (capped)' : ''} ·
            ' ' hashtag runs {report.hashtagRuns} / CSE queries {report.queriesRun} ·
            ' ' posts scanned {report.postsScanned} · found {report.candidatesFound} ·
            ' ' already known {report.alreadyKnown} · considered {report.considered} ·
            ' ' created {report.seriesCreated + report.venuesCreated} · dismissed {report.dismissed}
            {report.llmUsed ? '' : ' · classifier skipped'}
          </div>

          {report.created.length > 0 && (
            <table className="table table-sm align-middle mb-2">
              <thead>
                <tr><th scope="col">Candidate</th><th scope="col">Kind</th><th scope="col">Topic</th><th scope="col" className="text-end">Confidence</th></tr>
              </thead>
              <tbody>
                {report.created.map((c) => (
                  <tr key={c.rowKey}>
                    <td>{c.handle ? <code>@{c.handle}</code> : c.name}</td>
                    <td>{c.kind}</td>
                    <td>{c.topic}</td>
                    <td className="text-end">{c.confidence}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {report.created.length === 0 && (
            <div className="small">No new candidates this run.</div>
          )}

          {report.errors.length > 0 && (
            <details className="mt-2">
              <summary className="small text-warning">{report.errors.length} error(s)</summary>
              <ul className="small mb-0">
                {report.errors.slice(0, 20).map((e, i) => (
                  <li key={i}><code>{e.query}</code> — {e.message}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </section>
  );
}
